import { useEffect, useState, type RefObject } from "react";

export type ConversationStickyLevel = 1 | 2 | 3;

const conversationTurnSelector = "[data-conversation-turn-id]";
const conversationTurnSentinelSelector =
  "[data-conversation-turn-sentinel]";
const conversationUserCardSelector = "[data-conversation-user-card]";

function readStickyLineOffset(scrollContainer: HTMLElement) {
  const userCard =
    scrollContainer.querySelector<HTMLElement>(conversationUserCardSelector);
  if (!userCard || getComputedStyle(userCard).position !== "sticky") return 0;

  const offset = Number.parseFloat(getComputedStyle(userCard).top);
  return Number.isFinite(offset) ? Math.max(0, offset) : 0;
}

export function useConversationStickyState(
  scrollContainerRef: RefObject<HTMLElement | null>,
  titleSentinelRef: RefObject<HTMLElement | null>,
  turnSignature: string,
) {
  const [state, setState] = useState<{
    activeTurnId: string | null;
    level: ConversationStickyLevel;
  }>({ activeTurnId: null, level: 1 });

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    const titleSentinel = titleSentinelRef.current;
    if (!scrollContainer || !titleSentinel) return;

    let stickyLineOffset = readStickyLineOffset(scrollContainer);
    const recompute = () => {
      const rootTop = scrollContainer.getBoundingClientRect().top;
      const titlePassed = titleSentinel.getBoundingClientRect().bottom <= rootTop;
      const stickyLine = rootTop + stickyLineOffset;
      let activeTurnId: string | null = null;
      for (const sentinel of scrollContainer.querySelectorAll<HTMLElement>(
        conversationTurnSentinelSelector,
      )) {
        const turn = sentinel.closest<HTMLElement>(conversationTurnSelector);
        if (!turn) continue;
        const sentinelTop = sentinel.getBoundingClientRect().top;
        const turnBottom = turn.getBoundingClientRect().bottom;
        if (sentinelTop <= stickyLine && turnBottom > stickyLine) {
          activeTurnId = turn.dataset.conversationTurnId ?? null;
        }
      }
      const level: ConversationStickyLevel = activeTurnId
        ? 3
        : titlePassed
          ? 2
          : 1;
      setState((current) =>
        current.level === level && current.activeTurnId === activeTurnId
          ? current
          : { activeTurnId, level },
      );
    };

    const titleObserver = new IntersectionObserver(recompute, {
      root: scrollContainer,
      threshold: [0, 1],
    });
    const createTurnObserver = () =>
      new IntersectionObserver(recompute, {
        root: scrollContainer,
        rootMargin: `-${stickyLineOffset}px 0px -70% 0px`,
        threshold: [0, 1],
      });
    let turnObserver = createTurnObserver();
    const observeTurnSentinels = () => {
      for (const sentinel of scrollContainer.querySelectorAll<HTMLElement>(
        conversationTurnSentinelSelector,
      )) {
        turnObserver.observe(sentinel);
      }
    };
    titleObserver.observe(titleSentinel);
    observeTurnSentinels();
    const resizeObserver = new ResizeObserver(() => {
      const nextStickyLineOffset = readStickyLineOffset(scrollContainer);
      if (nextStickyLineOffset !== stickyLineOffset) {
        stickyLineOffset = nextStickyLineOffset;
        turnObserver.disconnect();
        turnObserver = createTurnObserver();
        observeTurnSentinels();
      }
      recompute();
    });
    resizeObserver.observe(scrollContainer);
    scrollContainer.addEventListener("scroll", recompute, { passive: true });
    recompute();

    return () => {
      resizeObserver.disconnect();
      scrollContainer.removeEventListener("scroll", recompute);
      titleObserver.disconnect();
      turnObserver.disconnect();
    };
  }, [scrollContainerRef, titleSentinelRef, turnSignature]);

  return state;
}
